import { useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "iframe",
  "summary",
  "[tabindex]",
  "[contenteditable=\"true\"]",
  "audio[controls]",
  "video[controls]",
].join(", ");

const ORIGINAL_TABINDEX_ATTR = "data-original-tabindex";

const rememberTabindex = (element) => {
  if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTR)) {
    const originalTabindex = element.getAttribute("tabindex");
    element.setAttribute(
      ORIGINAL_TABINDEX_ATTR,
      originalTabindex === null ? "" : originalTabindex
    );
  }
};

const restoreTabindex = (element) => {
  if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTR)) {
    return;
  }

  const originalTabindex = element.getAttribute(ORIGINAL_TABINDEX_ATTR);

  if (originalTabindex === "") {
    element.removeAttribute("tabindex");
  } else if (element.getAttribute("tabindex") !== originalTabindex) {
    element.setAttribute("tabindex", originalTabindex);
  }

  element.removeAttribute(ORIGINAL_TABINDEX_ATTR);
};

const setInertState = (element, isHidden) => {
  if ("inert" in element) {
    element.inert = isHidden;
  }

  if (isHidden) {
    if (!element.hasAttribute("inert")) {
      element.setAttribute("inert", "");
    }
    return;
  }

  if (element.hasAttribute("inert")) {
    element.removeAttribute("inert");
  }
};

const syncSlideAccessibility = (slide) => {
  const isHidden = slide.getAttribute("aria-hidden") === "true";

  setInertState(slide, isHidden);

  if (isHidden) {
    if (slide.hasAttribute("tabindex")) {
      rememberTabindex(slide);
      slide.removeAttribute("tabindex");
    }
  } else {
    restoreTabindex(slide);
  }

  slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
    if (isHidden) {
      rememberTabindex(element);
      if (element.getAttribute("tabindex") !== "-1") {
        element.setAttribute("tabindex", "-1");
      }
      return;
    }

    restoreTabindex(element);
  });
};

const useSlickAccessibility = (containerRef) => {
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    let frameId = null;

    const scheduleSync = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        container.querySelectorAll(".slick-slide").forEach(syncSlideAccessibility);
      });
    };

    scheduleSync();

    const observer = new MutationObserver(scheduleSync);
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-hidden", "tabindex", "class"],
    });

    return () => {
      observer.disconnect();

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [containerRef]);
};

export default useSlickAccessibility;
